// src/components/SimpleBreadcrumb/SimpleBreadcrumb.tsx
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, Flex } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { Optional } from '@/model/utils';
import { homeBreadcrumbItem } from './constants';
import { useTranslation } from 'react-i18next';
import { startCase } from 'lodash';

interface BreadcrumbInfoItem {
  link: string;
  label: string;
}

export type BreadcrumbInfoItems = [...BreadcrumbInfoItem[], Optional<BreadcrumbInfoItem, 'link'>];

export interface SimpleBreadcrumbProps {
  items: BreadcrumbInfoItems;
}

export const SimpleBreadcrumb = ({ items: providedItems }: SimpleBreadcrumbProps) => {
  const { t } = useTranslation();

  const items = [homeBreadcrumbItem, ...providedItems];
  const backDestination = items[Math.max(items.length - 2, 0)].link;
  const shouldShowBackButton = Boolean(items.length >= 2 && backDestination);

  return (
    <Flex align='center' gap='kilo'>
      <Breadcrumb separator={<ChevronRightIcon color='text.main' w={4} />} sx={{ ol: { margin: 0 } }}>
        {items.map(({ link = '', label }, index) => {
          const isFirstItem = index === 0;
          const isLastItem = index === items.length - 1;
          const shouldDisablePointerEvents = isLastItem || !link;

          return (
            <BreadcrumbItem key={`${link}-${label}`} isCurrentPage={isLastItem}>
              <BreadcrumbLink
                as={Link}
                to={link}
                pointerEvents={shouldDisablePointerEvents ? 'none' : 'all'}
                {...(isFirstItem
                  ? {
                      color: 'brand.500',
                      _hover: { color: 'brand.600' },
                    }
                  : {
                      color: 'text.main',
                      _hover: { color: 'text.mainHover' },
                      fontWeight: 500,
                    })}
              >
                {label ? startCase(label.toLowerCase()) : ''}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>

      {shouldShowBackButton && (
        <Button
          variant='buttonLink'
          as={Link}
          to={backDestination}
          size='xs'
          color='text.main'
          _hover={{ color: 'text.main', textDecor: 'underline' }}
        >
          {t('components.simple_breadcrumb.simple_breadcrumb_back_button')}
        </Button>
      )}
    </Flex>
  );
};
